<template>
  <a-drawer
    :visible="visible"
    title="添加药品/健康用品"
    :width="500"
    :drawerStyle="{ display: 'flex', flexDirection: 'column' }"
    :bodyStyle="{ padding: '16px', flex: 1, overflow: 'hidden' }"
    @close="handleDrawerClose"
  >
    <div class="drawer-content">
      <div class="drawer-header">
        <a-input
          v-model:value="keyword"
          allowClear
          placeholder="搜索药品名、诊断、证型、方名"
          @pressEnter="handleSearch"
          :disabled="searching"
        >
          <template #prefix>
            <SearchOutlined :style="{ color: '#999' }" />
          </template>
        </a-input>
      </div>

      <a-spin :spinning="searching" size="large">
        <div class="drawer-body">
          <template v-if="visibleSections.length">
            <div class="section" v-for="section in visibleSections" :key="section.key">
              <div class="section-title">
                <span>{{ section.title }}</span>
                <a-button
                  v-if="sectionHasNext[section.key]"
                  class="more-action"
                  type="link"
                  size="small"
                  @click="handleMore(section)"
                >
                  更多
                  <RightOutlined :style="{ fontSize: '10px' }" />
                </a-button>
              </div>

              <MedicineListSection
                :items="section.items"
                :render="section.render"
                :get-recommend-multiplier="getRecommendMultiplier"
                @select-sku="handleSelectSku"
                @select-protocol="handleSelectProtocol"
                @select-recommend="handleSelectRecommend"
              />
            </div>
          </template>
          <a-empty v-else description="暂无数据" />
        </div>
      </a-spin>
    </div>
  </a-drawer>

  <a-drawer
    :visible="moreVisible"
    :title="`更多${moreTitle}`"
    placement="right"
    :width="500"
    :drawerStyle="{ display: 'flex', flexDirection: 'column' }"
    :bodyStyle="{ padding: '16px', flex: 1, overflow: 'hidden' }"
    @close="closeMore"
  >
    <div class="drawer-content">
      <a-spin :spinning="moreLoading" size="large">
        <div class="drawer-body">
          <template v-if="moreItems.length">
            <MedicineListSection
              :items="moreItems"
              :render="moreRender"
              :get-recommend-multiplier="getRecommendMultiplier"
              @select-sku="handleSelectSku"
              @select-protocol="handleSelectProtocol"
              @select-recommend="handleSelectRecommend"
            />
          </template>
          <a-empty v-else description="暂无数据" />
          <div class="more-footer" v-if="moreHasNext">
            <a-button type="primary" ghost :loading="moreLoading" @click="handleLoadMore">
              加载更多
            </a-button>
          </div>
        </div>
      </a-spin>
    </div>
  </a-drawer>
</template>

<script setup>
import { defineEmits, defineProps, ref, computed } from 'vue'
import { useStore } from 'vuex'
import { message } from 'ant-design-vue'
import { aggregateSearch, prescriptionInfo } from '@/api/HealthRecord'
import MedicineListSection from './MedicineListSection.vue'
import { RightOutlined, SearchOutlined } from '@ant-design/icons-vue'
import { over } from 'lodash'

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['close', 'more'])
const store = useStore()
const storeDoctorId = computed(() => store.state.preMedicalRecord?.doctorId ?? null)
const storeUcDoctorId = computed(() => store.state.preMedicalRecord?.ucDoctorId ?? null)

const keyword = ref('')
const searching = ref(false)
// CHINESE_AND_WESTERN_MEDICINE(1, "中西药品"),
// HEALTH_MEDICINE(4, "健康药品"),
// PROTOCOL_PRESCRIPTION(7, "协定方"),
// RECOMMENDED_PRESCRIPTION(8, "推荐处方"),
// COMMON_PRESCRIPTION(9, "常用方"),
// SIMILAR_MEDICINE(10, "相似药品"),
// COMBINATION_PRESCRIPTION(11, "推荐方");

// 中西药集合
const CHINESE_AND_WESTERN_MEDICINE = ref([])
// 健康药品集合
const HEALTH_MEDICINE = ref([])
// 协定方集合
const PROTOCOL_PRESCRIPTION = ref([])
// 推荐处方集合
const RECOMMENDED_PRESCRIPTION = ref([])
// 常用方集合
const COMMON_PRESCRIPTION = ref([])
// 相似药品集合
const SIMILAR_MEDICINE = ref([])
// 推荐方集合
const COMBINATION_PRESCRIPTION = ref([])

// 各类型是否存在更多数据
const sectionHasNext = ref({})

const medicineTypeConfig = {
  1: { key: 'westernMedicine', render: 'medicine', title: '中西药品', sectionKey: 'medicine' },
  4: { key: 'healthMedicine', render: 'medicine', title: '健康用品', sectionKey: 'health' },
  7: { key: 'protocolPrescription', render: 'protocol', title: '协定方', sectionKey: 'protocol' },
  8: {
    key: 'recommendedPrescription',
    render: 'combination',
    title: '推荐处方',
    sectionKey: 'recommended'
  },
  9: { key: 'commonPrescription', render: 'protocol', title: '常用方', sectionKey: 'common' },
  10: { key: 'similarMedicine', render: 'medicine', title: '相似药品', sectionKey: 'similar' },
  11: {
    key: 'combinationPrescription',
    render: 'combination',
    title: '推荐方',
    sectionKey: 'combination'
  }
}

const sectionTypeByKey = {
  medicine: 1,
  health: 4,
  protocol: 7,
  recommended: 8,
  common: 9,
  similar: 10,
  combination: 11
}

const getTypeResult = (response, medicineType) => {
  const config = medicineTypeConfig[medicineType]
  if (!config) return { list: [], hasNext: false }
  if (config.key === 'commonPrescription') {
    const target = response.find(item => item.medicineType === 9)?.commonPrescription
      ?.westernOrChinesePrescription
    return {
      list: target?.list || target?.result || [],
      hasNext: Boolean(target?.hasNext)
    }
  }
  const target = response.find(item => item.medicineType === medicineType)?.[config.key]
  if (!target) return { list: [], hasNext: false }
  return {
    list: target.list || target.result || [],
    hasNext: Boolean(target.hasNext)
  }
}

const moreVisible = ref(false)
const moreTitle = ref('')
const moreType = ref(null)
const moreItems = ref([])
const moreHasNext = ref(false)
const morePage = ref(1)
const moreNum = ref(20)
const moreLoading = ref(false)
const moreRender = computed(() => medicineTypeConfig[moreType.value]?.render || 'medicine')

// 搜索药品并按类型分组写入列表
const handleSearch = async () => {
  try {
    searching.value = true
    const params = {
      doctorId: storeDoctorId.value || null,
      searchSource: 1,
      num: 20,
      page: 1,
      key: keyword.value,
      ucDoctorId: storeUcDoctorId.value || null
    }
    const response = (await aggregateSearch(params)) || []
    const westernResult = getTypeResult(response, 1)
    CHINESE_AND_WESTERN_MEDICINE.value = westernResult.list
    sectionHasNext.value.medicine = westernResult.hasNext

    const healthResult = getTypeResult(response, 4)
    HEALTH_MEDICINE.value = healthResult.list
    sectionHasNext.value.health = healthResult.hasNext

    const protocolResult = getTypeResult(response, 7)
    PROTOCOL_PRESCRIPTION.value = protocolResult.list
    sectionHasNext.value.protocol = protocolResult.hasNext

    const recommendedResult = getTypeResult(response, 8)
    RECOMMENDED_PRESCRIPTION.value = recommendedResult.list
    sectionHasNext.value.recommended = recommendedResult.hasNext

    const commonResult = getTypeResult(response, 9)
    COMMON_PRESCRIPTION.value = commonResult.list
    sectionHasNext.value.common = commonResult.hasNext

    const similarResult = getTypeResult(response, 10)
    SIMILAR_MEDICINE.value = similarResult.list
    sectionHasNext.value.similar = similarResult.hasNext

    const combinationResult = getTypeResult(response, 11)
    COMBINATION_PRESCRIPTION.value = combinationResult.list
    sectionHasNext.value.combination = combinationResult.hasNext
  } catch (error) {
    console.error('药品搜索失败:', error)
  } finally {
    searching.value = false
  }
}

// 页面分区配置：不同类型使用不同渲染模板
const sections = computed(() => [
  {
    key: 'medicine',
    title: '中西药品',
    items: CHINESE_AND_WESTERN_MEDICINE.value,
    render: 'medicine'
  },
  {
    key: 'health',
    title: '健康用品',
    items: HEALTH_MEDICINE.value,
    render: 'medicine'
  },
  {
    key: 'protocol',
    title: '协定方',
    items: PROTOCOL_PRESCRIPTION.value,
    render: 'protocol'
  },
  // {
  //   key: 'recommended',
  //   title: '推荐处方',
  //   items: RECOMMENDED_PRESCRIPTION.value,
  //   render: 'protocol'
  // },
  //   {
  //     key: 'common',
  //     title: '常用方',
  //     items: COMMON_PRESCRIPTION.value,
  //     render: 'protocol'
  //   },
  // {
  //   key: 'similar',
  //   title: '相似药品',
  //   items: SIMILAR_MEDICINE.value,
  //   render: 'medicine'
  // },
  {
    key: 'combination',
    title: '推荐方',
    items: COMBINATION_PRESCRIPTION.value,
    render: 'combination'
  }
])

// 写入 store 的 skuInfoList
const updateSkuInfoList = nextList => {
  store.dispatch('preMedicalRecord/updatePreMedicalRecord', {
    skuInfoList: nextList
  })
}

// 合并 sku 列表：skuCode 相同则数量累加
const addSkuListToStore = skuList => {
  if (!Array.isArray(skuList) || skuList.length === 0) return
  const currentList = store.state.preMedicalRecord?.skuInfoList || []
  const nextList = [...currentList]

  // 排除协定方(7)和健康辅助方(4)，只计算可配置数量限制的药品
  const nonProtocolCodes = new Set(
    currentList.filter(item => item.skuType !== 7 && item.skuType !== 4).map(item => item.skuCode)
  )
  const nextNonProtocolCodes = new Set(nonProtocolCodes)
  skuList.forEach(sku => {
    if (sku?.skuType !== 7 && sku?.skuType !== 4 && sku?.skuCode) {
      nextNonProtocolCodes.add(sku.skuCode)
    }
  })
  if (nextNonProtocolCodes.size > 5) {
    message.warning('最多添加五种药品')
    return false
  }

  skuList.forEach(sku => {
    const existingIndex = nextList.findIndex(entry => entry.skuCode === sku.skuCode)
    if (existingIndex >= 0) {
      const current = nextList[existingIndex]
      nextList[existingIndex] = {
        ...current,
        quantity: (current.quantity || 0) + (sku.quantity || 1)
      }
      return
    }
    nextList.push({
      skuCode: sku.skuCode,
      skuName: sku.skuName,
      quantity: sku.quantity || 1,
      // skuId: sku.skuId || null,
      skuType: sku.skuType === 5 ? 1 : sku.skuType ?? null // 将成药类型 5 归为中西药 1
    })
  })

  updateSkuInfoList(nextList)
  return true
}

// 中西药/健康用品/相似药品：标准商品结构转 skuInfo
const buildSkuFromMedicine = item => {
  if (!item?.skuCode) return null
  return {
    skuCode: item.skuCode,
    skuName: item.skuName || item.name || '',
    skuId: item.skuId || null,
    quantity: 1,
    skuType: item.skuType ?? item.productType ?? null
  }
}

// 推荐方明细：明细列表转 skuInfo
const buildSkuListFromDetails = (details = [], prescriptions = [], multiplier = 1) => {
  const safeMultiplier = Number(multiplier) || 1
  const detailList = Array.isArray(details) ? details : []
  const prescriptionList = Array.isArray(prescriptions) ? prescriptions : []
  const skuList = detailList
    .map(detail => ({
      skuCode: detail.skuCode,
      skuName: detail.skuName || detail.name || '',
      quantity: (detail.quantity || 1) * safeMultiplier,
      skuType: detail.skuType ?? detail.productType ?? null,
      skuId: detail.skuId || null
    }))
    .filter(item => item.skuCode)
  const agreementList = prescriptionList
    .map(item => ({
      skuCode: item.prescriptionSn,
      skuName: item.prescriptionName || item.name || '',
      quantity: (item.quantity || 1) * safeMultiplier,
      skuType: 7
    }))
    .filter(item => item.skuCode)
  return [...skuList, ...agreementList]
}

// 选用单个商品
const handleSelectSku = item => {
  const sku = buildSkuFromMedicine(item)
  if (!sku) return
  const added = addSkuListToStore([sku])
  if (added) {
    message.success(`已加入${sku.skuName || '药品'}`)
  }
}

// 协定方：使用 prescriptionSn 作为 skuCode
const buildSkuFromProtocol = item => {
  if (!item?.prescriptionSn) return null
  return {
    skuCode: item.prescriptionSn,
    skuName: item.prescriptionName || '',
    quantity: 1,
    skuType: 7
  }
}

// 仅在 store 为空时写入诊断（最多 10 条）
const applyDiagnosisFromList = list => {
  if (!Array.isArray(list) || list.length === 0) return
  const currentDiagnosis = store.state.preMedicalRecord?.diagnosisVOList || []
  if (currentDiagnosis.length > 0) return
  store.dispatch('preMedicalRecord/updatePreMedicalRecord', {
    diagnosisVOList: list.slice(0, 10)
  })
}

// 仅在 store 为空时写入中医证型（最多 10 条）
const applySyndromeFromList = list => {
  if (!Array.isArray(list) || list.length === 0) return
  const currentChinese = store.state.preMedicalRecord?.chineseDiagnosisVOList || []
  if (currentChinese.length > 0) return
  store.dispatch('preMedicalRecord/updatePreMedicalRecord', {
    chineseDiagnosisVOList: list.slice(0, 10)
  })
}

// 选用协定方
const handleSelectProtocol = async item => {
  if (item?.prescriptionSn) {
    try {
      const { diagnoses, westernDiagnoses } = await prescriptionInfo({
        prescriptionSn: item.prescriptionSn,
        searchType: 1,
        serialNumber: '',
        ucDoctorId: storeUcDoctorId.value || null,
        roleId: 2
      })
      // 合并西医诊断/中医证型到预病历
      applyDiagnosisFromList(westernDiagnoses || [])
      applySyndromeFromList(diagnoses || [])
    } catch (error) {
      console.error('获取协定方详情失败:', error)
    }
  }
  const sku = buildSkuFromProtocol(item)
  if (!sku) return
  const added = addSkuListToStore([sku])
  if (added) {
    message.success(`已加入${sku.skuName || '协定方'}`)
  }
}

// 选用推荐方（组合方）
const handleSelectRecommend = item => {
  // 1) 合并推荐方里的【西医诊断】到预病历
  //   - 推荐方字段为 diagnosesVOList
  //   - 以 id 去重
  const rawWesternList = item?.diagnosesVOList || []
  applyDiagnosisFromList(rawWesternList)
  // 2) 合并推荐方里的【中医证型】到预病历
  //   - 推荐方字段为 chineseDiagnosisVOList
  //   - 以 id 去重
  const rawChineseList = item?.chineseDiagnosisVOList || []
  applySyndromeFromList(rawChineseList)
  const multiplier = getRecommendMultiplier(item)
  const skuList = buildSkuListFromDetails(
    item?.recommendedDetailVOList,
    item?.prescriptionVOList,
    multiplier
  )
  const added = addSkuListToStore(skuList)
  if (added && skuList.length) {
    message.success(`已加入${item?.businessName || '推荐方'}`)
  }
}

// 推荐方数量倍数（默认 1）
const getRecommendMultiplier = item => {
  const value = Number(item?.recommendedAmount)
  return value && value > 0 ? value : 1
}

// 过滤空分组，避免无数据时展示分组
const visibleSections = computed(() =>
  sections.value.filter(section => Array.isArray(section.items) && section.items.length > 0)
)

// 点击“更多”
const handleMore = section => {
  const medicineType = sectionTypeByKey[section.key]
  if (!medicineType) return
  const config = medicineTypeConfig[medicineType]
  moreType.value = medicineType
  moreTitle.value = config?.title || ''
  morePage.value = 1
  moreItems.value = []
  moreHasNext.value = false
  moreVisible.value = true
  fetchMorePage(true)
  emit('more', section)
}

const fetchMorePage = async reset => {
  if (!moreType.value) return
  try {
    moreLoading.value = true
    if (reset) {
      morePage.value = 1
    }
    const params = {
      doctorId: storeDoctorId.value || null,
      searchSource: 1,
      num: moreNum.value,
      page: morePage.value,
      key: keyword.value,
      ucDoctorId: storeUcDoctorId.value || null,
      medicineType: moreType.value
    }
    const response = (await aggregateSearch(params)) || []
    const { list, hasNext } = getTypeResult(response, moreType.value)
    moreItems.value = reset ? list : [...moreItems.value, ...list]
    moreHasNext.value = hasNext
  } catch (error) {
    console.error('加载更多失败:', error)
  } finally {
    moreLoading.value = false
  }
}

const handleLoadMore = () => {
  if (moreLoading.value || !moreHasNext.value) return
  morePage.value += 1
  fetchMorePage(false)
}

const closeMore = () => {
  moreVisible.value = false
  moreItems.value = []
  moreHasNext.value = false
  moreLoading.value = false
  moreType.value = null
  moreTitle.value = ''
}

// 关闭主抽屉时清空搜索
const handleDrawerClose = () => {
  keyword.value = ''
  CHINESE_AND_WESTERN_MEDICINE.value = []
  HEALTH_MEDICINE.value = []
  PROTOCOL_PRESCRIPTION.value = []
  RECOMMENDED_PRESCRIPTION.value = []
  COMMON_PRESCRIPTION.value = []
  SIMILAR_MEDICINE.value = []
  COMBINATION_PRESCRIPTION.value = []
  sectionHasNext.value = {}
  emit('close')
}
</script>

<style scoped lang="scss">
.drawer-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  ::v-deep(.ant-spin-nested-loading) {
    overflow: auto;
  }
}

.drawer-header {
  margin-bottom: 12px;
  flex-shrink: 0;
  ::v-deep(.ant-input-affix-wrapper) {
    border-radius: 4px;
  }
}

::v-deep(.ant-spin) {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.drawer-body {
  padding: 8px 8px 16px;
  border-radius: 6px;
}

.section {
  margin-bottom: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 6px 0 11px;
  font-weight: 400;
  font-size: 15px;
  color: #666666;
  ::v-deep(.ant-btn) {
    display: flex;
    align-items: center;
    font-weight: 400;
    font-size: 14px;
    color: #999999;
  }
  ::v-deep(.ant-btn > span + .anticon) {
    margin-left: 3px;
  }
}

.more {
  color: #999;
  font-size: 12px;
}

.more-action {
  padding: 0;
  font-size: 14px;
}

.more-footer {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.drawer-empty {
  color: #999;
  font-size: 12px;
  padding: 8px 0;
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
}
</style>
